/**
 * ModalContext
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-20
 * 
 * Purpose: Global context to track when any modal is open
 * Used to hide floating buttons (WhatsApp, chat) when modals are displayed
 * 
 * Features:
 * - Track if any modal is currently open
 * - Provide methods to open/close modals with global state tracking
 * 
 * Dependencies:
 * - React 19.2.4 (createContext, useContext, useState)
 * 
 * Changelog:
 * - 1.0.0 (2025-03-20): Initial implementation
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

interface ModalContextType {
  isAnyModalOpen: boolean;
  openModal: (modalId: string) => void;
  closeModal: (modalId: string) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [openModals, setOpenModals] = useState<Set<string>>(new Set());

  const isAnyModalOpen = openModals.size > 0;

  const openModal = useCallback((modalId: string) => {
    setOpenModals(prev => new Set(prev).add(modalId));
  }, []);

  const closeModal = useCallback((modalId: string) => {
    setOpenModals(prev => {
      const newSet = new Set(prev);
      newSet.delete(modalId);
      return newSet;
    });
  }, []);

  return (
    <ModalContext.Provider value={{ isAnyModalOpen, openModal, closeModal }}>
      {children}
    </ModalContext.Provider>
  );
};
